import React from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Alert from '@material-ui/lab/Alert';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { TableMapDraft } from '../context';
import TableMapPreview from '../components/TableMapPreview';

export interface CreatedResult {
    id: string;
    name: string;
    tablesCount: number;
    totalSeats: number;
    status: 'Active' | 'Inactive';
    isActive: boolean;
    createdAt: string;
    createdBy: string;
    /** Labels of the tables the save left without the QR code they had in the editor. */
    qrReleasedTables: string[];
}

const GREEN = theme.palette.success.main;
const GREEN_BG = theme.palette.success.light;

const useStyles = makeStyles({
    root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    successBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '16px 20px',
        borderRadius: 8,
        backgroundColor: GREEN_BG,
        border: `1px solid ${GREEN}`,
    },
    successIcon: {
        color: GREEN,
        fontSize: 28,
        flexShrink: 0,
        marginTop: 2,
    },
    successTextGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    successTitle: {
        fontSize: 15,
        fontWeight: 700,
        color: GREEN,
    },
    successDesc: {
        fontSize: 13,
        color: GREEN,
        lineHeight: 1.4,
    },
    availabilityNote: {
        fontSize: 12,
        color: GREEN,
        lineHeight: 1.4,
        marginTop: 4,
        fontStyle: 'italic',
    },
    body: {
        display: 'flex',
        gap: 24,
        alignItems: 'flex-start',
    },
    previewCard: {
        flex: '1 1 0',
        minWidth: 0,
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    previewCanvas: {
        height: 360,
    },
    summaryCard: {
        width: 320,
        flexShrink: 0,
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    summaryCardHeader: {
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        backgroundColor: theme.palette.common.white,
    },
    cardHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    summaryRows: {
        padding: '8px 0',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    summaryValue: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: 'right' as const,
    },
    summaryByValue: {
        fontSize: 12,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'right' as const,
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
});

function formatCreatedAt(isoString: string): string {
    const d = new Date(isoString);
    const datePart = d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${datePart} - ${timePart}`;
}

interface CreatedStepProps {
    draft: TableMapDraft;
    createdResult: CreatedResult;
    isEdit?: boolean;
}

export default function CreatedStep({ draft, createdResult, isEdit }: CreatedStepProps) {
    const classes = useStyles();

    return (
        <div className={classes.root}>
            <div className={classes.successBanner}>
                <CheckCircleIcon className={classes.successIcon} />
                <div className={classes.successTextGroup}>
                    <Typography className={classes.successTitle}>
                        {isEdit
                            ? i18n.t('tableMaps.wizard.created.updatedSuccessTitle')
                            : i18n.t('tableMaps.wizard.created.createdSuccessTitle')}
                    </Typography>
                    <Typography className={classes.successDesc}>
                        {isEdit
                            ? createdResult.isActive
                                ? i18n.t('tableMaps.wizard.created.updatedSuccessActiveDesc', {
                                      name: createdResult.name,
                                  })
                                : i18n.t('tableMaps.wizard.created.updatedSuccessInactiveDesc', {
                                      name: createdResult.name,
                                  })
                            : createdResult.isActive
                            ? i18n.t('tableMaps.wizard.created.createdSuccessActiveDesc', {
                                  name: createdResult.name,
                              })
                            : i18n.t('tableMaps.wizard.created.createdSuccessInactiveDesc', {
                                  name: createdResult.name,
                              })}
                    </Typography>
                    {createdResult.isActive && (
                        <Typography className={classes.availabilityNote}>
                            {i18n.t('tableMaps.wizard.created.availabilityNote')}
                        </Typography>
                    )}
                </div>
            </div>

            {createdResult.qrReleasedTables.length > 0 && (
                <Alert severity="warning">
                    {i18n.t('tableMaps.wizard.tables.qrReleased', {
                        tables: createdResult.qrReleasedTables.join(', '),
                    })}
                </Alert>
            )}

            <div className={classes.body}>
                <div className={classes.previewCard}>
                    <div className={classes.summaryCardHeader}>
                        <Typography className={classes.cardHeading}>
                            {i18n.t('tableMaps.wizard.review.tableMapPreview')}
                        </Typography>
                    </div>
                    <div className={classes.previewCanvas}>
                        <TableMapPreview draft={draft} />
                    </div>
                </div>

                <div className={classes.summaryCard}>
                    <div className={classes.summaryCardHeader}>
                        <Typography className={classes.cardHeading}>
                            {i18n.t('tableMaps.wizard.review.tableMapSummary')}
                        </Typography>
                    </div>

                    <div className={classes.summaryRows}>
                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.tableMapName')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {createdResult.name}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.tablesCount')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {createdResult.tablesCount}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.totalSeats')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {createdResult.totalSeats}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.status')}
                            </Typography>
                            <div className={classes.statusCell}>
                                <span
                                    className={classes.statusDot}
                                    style={{
                                        backgroundColor: createdResult.isActive
                                            ? theme.palette.success.main
                                            : theme.palette.secondary.main,
                                    }}
                                />
                                <Typography className={classes.summaryValue}>
                                    {createdResult.isActive
                                        ? i18n.t('tableMaps.wizard.review.active')
                                        : i18n.t('tableMaps.wizard.review.inactive')}
                                </Typography>
                            </div>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.created.createdAtLabel')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {formatCreatedAt(createdResult.createdAt)}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <span />
                            <Typography className={classes.summaryByValue}>
                                {i18n.t('tableMaps.wizard.created.createdByPrefix')}{' '}
                                {createdResult.createdBy}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
